import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Image, Nav, Tab } from "react-bootstrap";
import {
  FaSave,
  FaTimes,
  FaUpload,
  FaFilePdf,
  FaTrash,
  FaEdit,
  FaEye,
  FaHeading,
  FaBold,
  FaItalic,
  FaListUl,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CourseService from "../../../services/courseService";

// Simple Editor - NO EXTERNAL PACKAGES NEEDED
const SimpleEditor = ({ value, onChange, placeholder }) => {
  const [activeTab, setActiveTab] = useState("write");

  const formatText = (text) => {
    if (!text) return "<p>No content yet. Start writing in the Write tab.</p>";

    let html = text;

    // Convert ### Heading to <h3>
    html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");

    // Convert bullet points
    html = html.replace(/^\- (.*$)/gim, "<li>$1</li>");
    let bulletMatch = html.match(/<li>.*<\/li>/g);
    if (bulletMatch) {
      bulletMatch.forEach((match) => {
        if (!html.includes("<ul>")) {
          html = html.replace(match, `<ul>${match}</ul>`);
        }
      });
    }

    // Convert bold **text**
    html = html.replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>");

    // Convert italic *text*
    html = html.replace(/\*(.*?)\*/gim, "<em>$1</em>");

    // Convert line breaks
    html = html.replace(/\n/g, "<br/>");

    return html;
  };

  const insertFormat = (type) => {
    const textarea = document.getElementById("long_description_textarea");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = "";
    let cursorOffset = 0;

    switch (type) {
      case "h3":
        newText = `### ${selectedText}`;
        cursorOffset = 4;
        break;
      case "bold":
        newText = `**${selectedText}**`;
        cursorOffset = 2;
        break;
      case "italic":
        newText = `*${selectedText}*`;
        cursorOffset = 1;
        break;
      case "list":
        newText = `- ${selectedText}`;
        cursorOffset = 2;
        break;
      default:
        return;
    }

    const newValue = value.substring(0, start) + newText + value.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, end + cursorOffset);
    }, 10);
  };

  return (
    <div>
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <div className="mb-2 d-flex justify-content-between align-items-center flex-wrap">
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link eventKey="write">
                <FaEdit className="me-1" /> Write
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="preview">
                <FaEye className="me-1" /> Preview
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === "write" && (
            <div className="mt-2 mt-sm-0">
              <Button
                size="sm"
                variant="outline-primary"
                className="me-1"
                onClick={() => insertFormat("h3")}
                title="Heading 3 (Module Name)"
              >
                <FaHeading /> H3
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                className="me-1"
                onClick={() => insertFormat("bold")}
                title="Bold"
              >
                <FaBold />
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                className="me-1"
                onClick={() => insertFormat("italic")}
                title="Italic"
              >
                <FaItalic />
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => insertFormat("list")}
                title="Bullet List"
              >
                <FaListUl />
              </Button>
            </div>
          )}
        </div>

        <div className="editor-help mb-2 p-2 bg-light rounded small">
          <strong>📝 Quick Guide:</strong>
          <span className="ms-2">### Module Name → Heading 3</span>
          <span className="ms-2">**bold text** → Bold</span>
          <span className="ms-2">*italic text* → Italic</span>
          <span className="ms-2">- item → Bullet list</span>
        </div>

        <Tab.Content>
          <Tab.Pane eventKey="write">
            <Form.Control
              id="long_description_textarea"
              as="textarea"
              rows={15}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              style={{
                fontFamily: "monospace",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="preview">
            <div
              className="border p-3 rounded bg-light"
              style={{
                minHeight: "300px",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: formatText(value) }} />
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

// Main CourseForm Component
const CourseForm = ({ course, onSuccess, onCancel }) => {
  const isEditMode = !!course;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    long_description: "",
    price: "",
    base_student_count: 0,
    duration: "",
    priority: 0,
    meta_title: "",
    meta_description: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [existingSyllabus, setExistingSyllabus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (course) {
      setFormData({
        name: course.name || "",
        description: course.description || "",
        long_description: course.long_description || "",
        price: course.price || "",
        base_student_count: course.base_student_count || 0,
        duration: course.duration || "",
        priority: course.priority || 0,
        meta_title: course.meta_title || "",
        meta_description: course.meta_description || "",
      });
      if (course.image_url) {
        setImagePreview(course.image_url);
      }
      if (course.syllabus_name) {
        setExistingSyllabus({
          name: course.syllabus_name,
          path: course.syllabus_path,
        });
      }
    }
  }, [course]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleLongDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, long_description: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, PNG, GIF, and WEBP images are allowed");
        return;
      }
      if (file.size > 2097152) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      setImageFile(file); // ← Saves original file
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSyllabusChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed for syllabus");
        return;
      }
      if (file.size > 5242880) {
        toast.error("Syllabus size must be less than 5MB");
        return;
      }
      setSyllabusFile(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Course name is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.price) newErrors.price = "Price is required";
    if (!formData.duration.trim()) newErrors.duration = "Duration is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("long_description", formData.long_description || "");
      submitData.append("price", formData.price);
      submitData.append("base_student_count", formData.base_student_count);
      submitData.append("duration", formData.duration);
      submitData.append("priority", formData.priority);
      submitData.append("meta_title", formData.meta_title || "");
      submitData.append("meta_description", formData.meta_description || "");

      if (imageFile) {
        submitData.append("image", imageFile);
      }

      if (syllabusFile) {
        submitData.append("syllabus", syllabusFile);
      }

      if (isEditMode) {
        submitData.append("_method", "PUT");
        await CourseService.updateCourseWithFiles(course.id, submitData);
        toast.success("Course updated successfully!");
      } else {
        await CourseService.createCourseWithFiles(submitData);
        toast.success("Course created successfully!");
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error saving course:", error);
      toast.error(error.response?.data?.message || "Failed to save course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={8}>
          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Basic Information</h5>

              <Form.Group className="mb-3">
                <Form.Label>
                  Course Title <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter course title"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Price (₹) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Enter course price"
                      isInvalid={!!errors.price}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.price}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Base Student Count (Already Enrolled)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="base_student_count"
                      value={formData.base_student_count}
                      onChange={handleChange}
                      placeholder="Enter number of students already enrolled"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Duration <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="e.g., 12 weeks, 30 hours"
                      isInvalid={!!errors.duration}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.duration}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority (Order)</Form.Label>
                    <Form.Control
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      placeholder="Lower number appears first"
                    />
                    <Form.Text className="text-muted">
                      Courses with lower priority number appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  Short Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief overview of the course"
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Long Description (Detailed Course Content)
                </Form.Label>
                <SimpleEditor
                  value={formData.long_description}
                  onChange={handleLongDescriptionChange}
                  placeholder={`### Module 1: Introduction to Programming

This module covers the basics of programming...

### Module 2: Variables and Data Types

Learn about different data types...

### Module 3: Control Flow

- If statements
- Loops
- Switch cases`}
                />
                <Form.Text className="text-muted mt-2">
                  <strong>💡 Tip:</strong> Use "###" for module names. These
                  become clickable accordion modules!
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">SEO Settings</h5>

              <Form.Group className="mb-3">
                <Form.Label>Meta Title</Form.Label>
                <Form.Control
                  type="text"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="SEO title (recommended: 50-60 characters)"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Meta Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="SEO description (recommended: 150-160 characters)"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Course Image</h5>

              {imagePreview ? (
                <div className="text-center mb-3">
                  <Image
                    src={imagePreview}
                    alt="Course preview"
                    className="img-fluid rounded"
                    style={{
                      maxHeight: "200px",
                      objectFit: "contain",
                      width: "100%",
                    }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="mt-2"
                    onClick={removeImage}
                  >
                    <FaTrash className="me-1" /> Remove Image
                  </Button>
                </div>
              ) : (
                <div className="text-center mb-3">
                  <div className="border rounded p-4 text-center">
                    <FaUpload size={40} className="text-muted mb-2" />
                    <p className="text-muted small">No image selected</p>
                  </div>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Upload Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageChange}
                />
                <Form.Text className="text-muted">
                  Max size: 2MB. Allowed: JPG, PNG, GIF, WEBP
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Syllabus (PDF)</h5>

              {existingSyllabus && !syllabusFile && (
                <div className="alert alert-info mb-3">
                  <FaFilePdf className="me-2" />
                  Current syllabus: {existingSyllabus.name}
                </div>
              )}

              {syllabusFile && (
                <div className="alert alert-success mb-3">
                  <FaFilePdf className="me-2" />
                  New syllabus selected: {syllabusFile.name}
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  {isEditMode
                    ? "Update Syllabus (Optional)"
                    : "Upload Syllabus (Optional)"}
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="application/pdf"
                  onChange={handleSyllabusChange}
                />
                <Form.Text className="text-muted">
                  Max size: 5MB. Only PDF files are allowed.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          <FaTimes className="me-2" /> Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <FaSave className="me-2" />{" "}
              {isEditMode ? "Update Course" : "Create Course"}
            </>
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CourseForm;
